import Mousetrap from 'mousetrap';
import { useCallback, useEffect } from 'react';
import { useModalStore } from 'src/store/modals';
import { StoreShortcut, useShortcutsStore } from 'src/store/shortcuts';

export const useRegisterShortcuts = (shortcuts: StoreShortcut | StoreShortcut[]) => {
  const addShortcut = useShortcutsStore((state) => state.addShortcut);
  const removeShortcut = useShortcutsStore((state) => state.removeShortcut);
  const getShortcutForCommand = useShortcutsStore((state) => state.getShortcutForCommand);
  const getAllShortcuts = useShortcutsStore((state) => state.getAllShortcuts);

  const modal = useModalStore((state) => state.modal);

  const updateShortcutForCommand = useCallback(
    (command: StoreShortcut['command']) => {
      const shortcut = getShortcutForCommand(command);
      if (shortcut?.callback && (!modal || (modal && shortcut.enabledInModal))) {
        Mousetrap.bind(command, shortcut.callback);
      } else {
        Mousetrap.unbind(command);
      }
    },
    [getShortcutForCommand, modal],
  );

  useEffect(() => {
    // disable all shortcuts without `enabledInModal` flag when modal is open
    const allCommands = Array.from(getAllShortcuts().keys());
    if (modal) {
      Mousetrap.unbind(
        allCommands
          .map((command) => {
            const shortcut = getShortcutForCommand(command);
            if (shortcut && !shortcut.enabledInModal) {
              return command;
            }
            return '';
          })
          .filter(Boolean),
      );
    } else {
      allCommands.forEach(updateShortcutForCommand);
    }
  }, [getAllShortcuts, getShortcutForCommand, modal, updateShortcutForCommand]);

  useEffect(() => {
    const shortcutArray = Array.isArray(shortcuts) ? shortcuts : [shortcuts];
    shortcutArray.forEach((shortcut) => {
      addShortcut(shortcut);
      updateShortcutForCommand(shortcut.command);
    });
    return () => {
      shortcutArray.forEach((shortcut) => {
        removeShortcut(shortcut);
        updateShortcutForCommand(shortcut.command);
      });
    };
  }, [shortcuts, addShortcut, removeShortcut, updateShortcutForCommand]);
};
